export const DRAFT_SOUND_MASTER_GAIN = 1.25;

export interface DraftChimeNote {
  frequency: number;
  offset: number;
  duration: number;
  peak: number;
}

// Exact Team Beer Draft Lab mock Standard Draft chimes. The source app uses
// Web Audio oscillators rather than audio files, so retaining these frequency,
// timing, waveform, envelope, and master-gain values is the lossless import.
export const ON_CLOCK_CHIME_NOTES: readonly DraftChimeNote[] = Object.freeze([
  Object.freeze({ frequency: 523.25, offset: 0, duration: 0.55, peak: 0.052 }),
  Object.freeze({ frequency: 659.25, offset: 0.14, duration: 0.58, peak: 0.047 }),
  Object.freeze({ frequency: 783.99, offset: 0.28, duration: 0.65, peak: 0.042 }),
]);

export const SELECTION_MADE_CHIME_NOTES: readonly DraftChimeNote[] = Object.freeze([
  Object.freeze({ frequency: 659.25, offset: 0, duration: 0.18, peak: 0.042 }),
  Object.freeze({ frequency: 987.77, offset: 0.09, duration: 0.34, peak: 0.034 }),
]);

interface AudioParamLike {
  value: number;
  setValueAtTime(value: number, startTime: number): void;
  exponentialRampToValueAtTime(value: number, endTime: number): void;
}

interface AudioNodeLike {
  connect(destination: unknown): unknown;
}

interface GainNodeLike extends AudioNodeLike {
  gain: AudioParamLike;
}

interface OscillatorNodeLike extends AudioNodeLike {
  type: OscillatorType;
  frequency: Pick<AudioParamLike, "setValueAtTime">;
  start(when?: number): void;
  stop(when?: number): void;
}

export interface DraftAudioContextLike {
  readonly currentTime: number;
  readonly destination: unknown;
  readonly state: AudioContextState;
  createGain(): GainNodeLike;
  createOscillator(): OscillatorNodeLike;
  resume(): Promise<void>;
  close?(): Promise<void>;
}

export type DraftAudioContextFactory = () => DraftAudioContextLike | null;

function defaultAudioContextFactory(): DraftAudioContextLike | null {
  const browserGlobal = globalThis as typeof globalThis & {
    webkitAudioContext?: typeof AudioContext;
  };
  const AudioContextClass = browserGlobal.AudioContext ?? browserGlobal.webkitAudioContext;
  if (!AudioContextClass) return null;
  try {
    return new AudioContextClass() as unknown as DraftAudioContextLike;
  } catch {
    return null;
  }
}

export interface KeeperDraftSoundController {
  prime(): void;
  playOnClock(): void;
  playSelectionMade(): void;
  dispose(): void;
}

export function createKeeperDraftSoundController(
  contextFactory: DraftAudioContextFactory = defaultAudioContextFactory,
): KeeperDraftSoundController {
  let context: DraftAudioContextLike | null = null;
  let masterGain: GainNodeLike | null = null;
  let disposed = false;

  const ensureContext = () => {
    if (disposed) return null;
    if (context && context.state !== "closed") return context;
    context = contextFactory();
    if (!context) return null;
    masterGain = context.createGain();
    masterGain.gain.value = DRAFT_SOUND_MASTER_GAIN;
    masterGain.connect(context.destination);
    return context;
  };

  const runWhenReady = (
    audioContext: DraftAudioContextLike,
    schedule: () => void,
  ) => {
    if (audioContext.state === "running") {
      schedule();
      return;
    }
    void audioContext.resume()
      .then(() => {
        if (!disposed && audioContext.state === "running") schedule();
      })
      .catch(() => {});
  };

  const play = (
    notes: readonly DraftChimeNote[],
    leadSeconds: number,
    waveform: (index: number) => OscillatorType,
    attackSeconds: number,
    stopPaddingSeconds: number,
  ) => {
    const audioContext = ensureContext();
    if (!audioContext) return;
    runWhenReady(audioContext, () => {
      const now = audioContext.currentTime + leadSeconds;
      notes.forEach((note, index) => {
        const start = now + note.offset;
        const end = start + note.duration;
        const oscillator = audioContext.createOscillator();
        const gain = audioContext.createGain();
        oscillator.type = waveform(index);
        oscillator.frequency.setValueAtTime(note.frequency, start);
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(note.peak, start + attackSeconds);
        gain.gain.exponentialRampToValueAtTime(0.0001, end);
        oscillator.connect(gain);
        gain.connect(masterGain ?? audioContext.destination);
        oscillator.start(start);
        oscillator.stop(end + stopPaddingSeconds);
      });
    });
  };

  return {
    prime() {
      const audioContext = ensureContext();
      if (audioContext && audioContext.state !== "running") {
        void audioContext.resume().catch(() => {});
      }
    },
    playOnClock() {
      play(ON_CLOCK_CHIME_NOTES, 0.02, () => "sine", 0.035, 0.03);
    },
    playSelectionMade() {
      play(
        SELECTION_MADE_CHIME_NOTES,
        0.015,
        (index) => index === 0 ? "triangle" : "sine",
        0.018,
        0.02,
      );
    },
    dispose() {
      disposed = true;
      const activeContext = context;
      context = null;
      masterGain = null;
      if (activeContext?.close && activeContext.state !== "closed") {
        void activeContext.close().catch(() => {});
      }
    },
  };
}
